/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './weather-station-dashboard-widget.scss';

/* eslint-disable import/no-unresolved, import/default */

import weatherStationDashboardWidgetTemplate from './weather-station-dashboard-widget.tpl.html';
import {displayTelemetryTooltip} from "../../../../../../utils/tooltip-utility";

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.weatherStationDashboardWidget', [])
    .directive('agWeatherStationDashboardWidget', WeatherStationDashboardWidget)
    .name;

/*@ngInject*/
function WeatherStationDashboardWidget() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            weatherStationId: '='
        },
        controller: WeatherStationDashboardWidgetController,
        controllerAs: 'vm',
        templateUrl: weatherStationDashboardWidgetTemplate
    };
}

/*@ngInject*/
function WeatherStationDashboardWidgetController($scope, $log, $translate, $compile, $filter, unitService, weatherStationService, agTypes, types) {

    let vm = this;

    vm.loading = true;
    vm.agTypes = agTypes;
    vm.displayTemperatureTooltip = displayTemperatureTooltip;
    vm.displayHumidityTooltip = displayHumidityTooltip;
    vm.tempHistoryUnits = unitService.temperatureUnitSymbol();
    vm.humidityHistoryUnits = unitService.moistureUnitSymbol();

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: function(subscription) {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };

    vm.tempColor = tempColor;
    vm.humColor = humColor;

    $scope.$watch('vm.weatherStationId', function() {
        vm.weatherStationDevice = null;
        reset();
        if (vm.weatherStationId) {
            vm.loading = true;
            weatherStationService.getWeatherStationDevices(vm.weatherStationId).then(
                function success(weatherStationDevices) {
                    for (var i=0;i<weatherStationDevices.length;i++) {
                        if (weatherStationDevices[i].type === agTypes.deviceType.weatherStation) {
                            vm.weatherStationDevice = weatherStationDevices[i];
                            break;
                        }
                    }
                    if (vm.weatherStationDevice) {
                        var latestValuesSubscriptionsInfo = [{
                            type: types.datasourceType.entity,
                            entityType: types.entityType.device,
                            entityId: vm.weatherStationDevice.id.id,
                            entityName: vm.weatherStationDevice.name,
                            timeseries: [
                                {name: vm.agTypes.telemetry.weatherStation.temperature},
                                {name: vm.agTypes.telemetry.weatherStation.humidity}
                            ]
                        }];
                        vm.latestValuesSubscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
                        vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
                        vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.limit = 1;
                        vm.ctx.subscriptionApi.createSubscriptionFromInfo (
                            types.widgetType.timeseries.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
                            function(subscription) {
                                vm.latestValuesSubscription = subscription;
                            }
                        );
                        vm.weatherStationTempHistorySubscriptionsInfo = [{
                            type: types.datasourceType.entity,
                            entityType: types.entityType.device,
                            entityId: vm.weatherStationDevice.id.id,
                            entityName: vm.weatherStationDevice.name,
                            timeseries: [
                                {
                                    name: agTypes.telemetry.weatherStation.temperature,
                                    label: $translate.instant('ag.weather-station.temperature'),
                                    color: agTypes.colors.maxColor,
                                    postFuncBody: unitService.temperaturePostprocessingFuncBody()
                                }
                            ]
                        }];

                        vm.weatherStationHumidityHistorySubscriptionsInfo = [{
                            type: types.datasourceType.entity,
                            entityType: types.entityType.device,
                            entityId: vm.weatherStationDevice.id.id,
                            entityName: vm.weatherStationDevice.name,
                            timeseries: [
                                {
                                    name: agTypes.telemetry.weatherStation.humidity,
                                    label: $translate.instant('ag.weather-station.humidity'),
                                    color: agTypes.colors.minColor
                                }
                            ]
                        }];
                    } else {
                        vm.loading = false;
                    }
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    });

    function tempColor(/*val*/) {
        return agTypes.colors.normalGreyColor;
    }

    function humColor(/*val*/) {
        return agTypes.colors.normalGreyColor;
    }

    $scope.$on('dashboardTimewindowChanged', function (event, newDashboardTimewindow) {
        if (vm.latestValuesSubscription) {
            var newTimewindowConfig = angular.copy(newDashboardTimewindow);
            newTimewindowConfig.aggregation.type = types.aggregation.none.value;
            newTimewindowConfig.aggregation.limit = 1;
            vm.latestValuesSubscription.timeWindowConfig = newTimewindowConfig;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.tempVal = 0;
        vm.humVal = 0;
        vm.temp = '';
        vm.hum = '';
    }

    function getFormattedTs(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][0])) {
            if (!isNaN(keyData.data[0][0])) {
                var ts = Number(keyData.data[0][0]);
                return $filter('date')(ts, 'yyyy-MM-dd HH:mm:ss');
            } else {
                return '';
            }
        } else {
            return '';
        }
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        for (var i=0;i<data.length;i++) {
            var datasourceData = data[i];
            var val = getNumberValue(datasourceData);
            var ts = getFormattedTs(datasourceData);

            switch (datasourceData.dataKey.name) {
                case vm.agTypes.telemetry.weatherStation.temperature:
                    vm.tempVal = val;
                    vm.tempTs = ts;
                    vm.temp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 0, '')+'°';
                    break;
                case vm.agTypes.telemetry.weatherStation.humidity:
                    vm.humVal = val;
                    vm.humTs = ts;
                    vm.hum = vm.ctx.utils.formatValue(val, 0, '')+'%';
                    break;
            }
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function getNumberValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

    function displayTemperatureTooltip($event) {
        displayTelemetryTooltip($event, $scope, $compile, vm.tempVal, vm.temp, vm.tempTs, vm.tempColor());
    }

    function displayHumidityTooltip($event) {
        displayTelemetryTooltip($event, $scope, $compile, vm.humVal, vm.hum, vm.humTs, vm.humColor());
    }
}